import { ColorScheme } from "../../../.storyblok/types/287059/storyblok-components";
import { useImageLoading } from "../../helpers/ImageLoadingContext";
import { analyticsFilter } from "../../helpers/analytics";
import { fallbackColorScheme } from "../../helpers/colorSchemes";
import { FlyOutData } from "../../helpers/data/getFlyOutData";
import { getImageSource } from "../../helpers/getImageSource";
import { getStoryblokImageUrl } from "../../helpers/images/getStoryblokImageUrl";
import { useMediaQuery } from "../../helpers/useMediaQuery";
import { ProductSquare } from "../ProductSquare/ProductSquare";
import { CosmosButton, CosmosIconClose, CosmosTitle } from "@cosmos/web-react";
import classNames from "classnames/bind";
import styles from "./RailFlyOver.module.css";

const cx = classNames.bind(styles);

export function RailFlyOver({
  title,
  categories,
  featuredProducts,
  onClose,
}: {
  title: string;
  featuredProducts: FlyOutData["featuredProducts"];
  categories: FlyOutData["categories"];
  onClose: () => void;
}) {
  const isDesktop = useMediaQuery("(width >= 650px)", {
    initializeWithValue: false,
  });

  return (
    <div className={cx("container")}>
      <div className={cx("header")}>
        <CosmosTitle
          appearance="dark"
          size="medium"
          tag="h2"
          className={cx("title")}
        >
          {title}
        </CosmosTitle>
        <CosmosButton
          onClick={onClose}
          className={cx("close-button")}
          size={isDesktop ? "large" : "small"}
          appearance="dark"
          kind="secondary"
          shape="circle"
          buttonAriaLabel="Close"
        >
          <CosmosIconClose className={cx("icon-close")} slot="icon" />
        </CosmosButton>
      </div>
      <div className={cx("inner")}>
        <div className={cx("featuredProducts")}>
          {featuredProducts.map((product, index) => (
            <FeaturedProduct
              key={index}
              title={product.title}
              graphicUrl={product.graphicUrl}
              graphicAlt={product.graphicAlt ?? ""}
              productId={product.productId}
              colorScheme={product.colorScheme}
              url={product.url}
              redBullVersion={product.redBullVersion}
            />
          ))}
        </div>
        {categories.map((category, index) => (
          <ProductsCategory
            key={index}
            title={category.title}
            products={category.products}
            url={category.url}
          />
        ))}
      </div>
    </div>
  );
}

function FeaturedProduct({
  productId,
  colorScheme = fallbackColorScheme,
  graphicUrl,
  graphicAlt,
  url,
  title,
  redBullVersion,
}: {
  productId: string;
  title: string;
  colorScheme: ColorScheme | undefined;
  graphicUrl: string;
  graphicAlt?: string;
  url: string;
  redBullVersion:
    | "original"
    | "sugarfree"
    | "zero"
    | "edition"
    | "edition-sugarfree"
    | undefined;
}) {
  const shouldUseWhiteBackground =
    redBullVersion === "original" ||
    redBullVersion === "sugarfree" ||
    redBullVersion === "zero";

  const backgroundStyle = {
    "--productBackgroundColor": shouldUseWhiteBackground
      ? "#ffffff"
      : colorScheme.secondaryColor,
    "--scheme-can-shadow-rgb": colorScheme.canShadowRGB,
  };

  const underlineStyle = {
    "--cosmos-title-color": shouldUseWhiteBackground
      ? "var(--cosmos-color-text-dark)"
      : colorScheme.textAppearance === "light"
        ? "var(--cosmos-color-text-light)"
        : "var(--cosmos-color-text-dark)",
  };

  const imageSource = graphicUrl ? getImageSource(graphicUrl) : null;

  const imageLoading = useImageLoading();

  return (
    <a
      className={cx("featuredProduct")}
      href={url}
      style={{ ...backgroundStyle, ...underlineStyle }}
      onClick={() => {
        analyticsFilter({
          value: productId,
          contentType: "range",
        });
      }}
    >
      <div className={cx("featuredProduct__clip")}>
        <div className={cx("featuredProduct__inner")}>
          <CosmosTitle
            className={cx("title")}
            appearance={
              shouldUseWhiteBackground ? "dark" : colorScheme.textAppearance
            }
            size="xx-small"
            tag="h3"
          >
            {title}
          </CosmosTitle>
          {imageSource === "storyblok" ? (
            <img
              className={cx("featuredProduct__image")}
              loading={imageLoading}
              src={getStoryblokImageUrl(graphicUrl, {
                width: 352,
                height: undefined,
              })}
              alt={graphicAlt ?? ""}
            />
          ) : (
            <img
              style={backgroundStyle}
              className={cx("featuredProduct__image")}
              loading={imageLoading}
              src={graphicUrl}
              alt={graphicAlt ?? ""}
            />
          )}
        </div>
      </div>
    </a>
  );
}

// TODO: I feel like we probably have a helper that can handle this?
const MaybeLink = ({
  children,
  href,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) =>
  href && href !== "#" ? (
    <a href={href} {...rest}>
      {children}
    </a>
  ) : (
    children
  );

function ProductsCategory({
  title,
  url,
  products,
}: {
  title: string;
  url: string | undefined;
  products: {
    colorScheme: ColorScheme | undefined;
    graphicUrl: string;
    grahicAlt?: string;
    url: string;
    productId: string;
    title: string;
    redBullVersion:
      | "original"
      | "sugarfree"
      | "zero"
      | "edition"
      | "edition-sugarfree"
      | undefined;
  }[];
}) {
  return (
    <div>
      <h2 className={cx("productsCategoryTitle")}>
        <MaybeLink
          className={cx("productsCategory")}
          /**
           * If these links are empty strings (which can happen if there's no
           * category page in this locale) then we don't want the wrapping link.
           */
          href={url}
          onClick={() => {
            analyticsFilter({ value: title, contentType: "range" });
          }}
        >
          <CosmosTitle appearance="dark" size="x-small" tag="span">
            {title}
          </CosmosTitle>
        </MaybeLink>
      </h2>
      <div className={cx("productsSection")}>
        {products.map((product, index) => {
          return (
            <a
              key={index}
              className={cx("productSquare")}
              href={product.url}
              onClick={() =>
                analyticsFilter({
                  value: product.productId,
                  contentType: "range",
                })
              }
            >
              <ProductSquare
                imageAlt={product.grahicAlt ?? ""}
                imageUrl={product.graphicUrl}
                colorScheme={product.colorScheme ?? fallbackColorScheme}
                title={product.title}
                redBullVersion={product.redBullVersion}
              />
            </a>
          );
        })}
      </div>
    </div>
  );
}
